/**
 * Portfolio Rebalancing Tool
 *
 * Helps maintain target asset allocations automatically
 * 100% READ-ONLY - No data modification
 *
 * Features:
 * - Set target allocations for each asset
 * - Calculate required trades to rebalance
 * - Visualize current vs target allocation
 * - Minimize trading fees with smart suggestions
 *
 * IMPORTANT: This tool only provides suggestions. User must manually execute trades.
 */

import { useState, useMemo, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import { StorageService } from '../services/storage';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import type { Token, Position, Prices } from '../types';

interface AssetAllocation {
  tokenId: string;
  tokenName: string;
  symbol: string;
  currentValue: number;
  currentPercent: number;
  targetPercent: number;
  difference: number;
  differencePercent: number;
  action: 'buy' | 'sell' | 'hold';
  amount: number;
  amountUSD: number;
}

interface RebalanceTrade {
  tokenId: string;
  tokenName: string;
  symbol: string;
  action: 'buy' | 'sell';
  quantity: number;
  valueUSD: number;
  currentPrice: number;
}

export function PortfolioRebalancing() {
  // 🔄 HYBRID MODE: Use Portfolio & Positions Hooks (localStorage OR Supabase)
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  const [tokens, setTokens] = useState<Token[]>([]);
  const [targetAllocations, setTargetAllocations] = useState<Record<string, number>>({});
  const [rebalanceThreshold, setRebalanceThreshold] = useState(5); // Rebalance if >5% off target
  const [showTrades, setShowTrades] = useState(false);

  // Load cached prices from localStorage (shared with Portfolio Dashboard)
  const [cachedPrices, setCachedPrices] = useState<Prices>({});
  useEffect(() => {
    const cachedData = StorageService.get<{ prices: Prices; timestamp: number }>('cached_prices');
    if (cachedData && cachedData.prices) {
      setCachedPrices(cachedData.prices);
    }
  }, []);

  // Fetch real-time prices from CoinGecko API (with React Query caching)
  const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);
  const { data: apiPrices = {}, isLoading: pricesLoading } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
    refetchInterval: 60000, // Refresh every 60s
  });

  // Merge API prices with cached prices (prefer API, fallback to cache)
  const prices = useMemo(() => {
    // If API is loading and we have cache, use cache as fallback
    if (pricesLoading && Object.keys(cachedPrices).length > 0) {
      return { ...cachedPrices, ...apiPrices };
    }
    return apiPrices;
  }, [apiPrices, cachedPrices, pricesLoading]);

  // Sync tokens from hybrid hooks (READ-ONLY)
  useEffect(() => {
    if (portfolioHook.loading || positionsHook.loading) return;
    if (!portfolioHook.activePortfolio) return;

    // Group positions by tokenId
    const positionsByToken = new Map<string, Position[]>();
    positionsHook.positions.forEach((hybridPos) => {
      const tokenId = 'token_id' in hybridPos ? hybridPos.token_id : 'tokenId' in hybridPos ? hybridPos.tokenId : '';
      const legacyPosition: Position = {
        id: hybridPos.id,
        type: hybridPos.type as 'buy' | 'sell',
        date: hybridPos.date,
        quantity: hybridPos.quantity,
        price: hybridPos.price,
        fee: hybridPos.fee || undefined,
        note: hybridPos.note || undefined,
        is_swap: (hybridPos as any).is_swap,
        swap_chain_id: (hybridPos as any).swap_chain_id,
        original_cost_basis: (hybridPos as any).original_cost_basis,
      };

      const existing = positionsByToken.get(tokenId) || [];
      positionsByToken.set(tokenId, [...existing, legacyPosition]);
    });

    // Convert hybrid tokens to legacy Token format
    const legacyTokens: Token[] = portfolioHook.tokens.map((hybridToken) => {
      const tokenId = 'coin_id' in hybridToken ? hybridToken.coin_id : hybridToken.id;
      const displayName = 'symbol' in hybridToken ? hybridToken.symbol : 'name' in hybridToken ? hybridToken.name : tokenId;

      return {
        id: tokenId,
        displayName: (displayName || tokenId || 'UNKNOWN').toUpperCase(),
        positions: positionsByToken.get(tokenId) || [],
        note: 'notes' in hybridToken ? hybridToken.notes : undefined,
        tags: [],
        targetPrice: hybridToken.targetPrice || hybridToken.target_price || undefined,
      };
    });

    setTokens(legacyTokens);

    // Initialize target allocations equally if not set
    if (legacyTokens.length > 0 && Object.keys(targetAllocations).length === 0) {
      const equalAllocation = 100 / legacyTokens.length;
      const initialTargets: Record<string, number> = {};
      legacyTokens.forEach((token: Token) => {
        initialTargets[token.id] = equalAllocation;
      });
      setTargetAllocations(initialTargets);
    }
  }, [
    portfolioHook.loading,
    portfolioHook.activePortfolio,
    portfolioHook.tokens,
    positionsHook.loading,
    positionsHook.positions,
  ]);

  // Calculate portfolio metrics
  const metrics = useMemo(() => {
    let totalValue = 0;
    const allocations: AssetAllocation[] = [];

    // First pass: calculate total value
    tokens.forEach(token => {
      const currentPrice = prices[token.id]?.usd || 0;
      let totalQuantity = 0;

      token.positions?.forEach(position => {
        if (position.type === 'buy') {
          totalQuantity += position.quantity;
        } else if (position.type === 'sell') {
          totalQuantity -= position.quantity;
        }
      });

      const currentValue = currentPrice * totalQuantity;
      totalValue += currentValue;
    });

    // Second pass: calculate allocations
    tokens.forEach(token => {
      const currentPrice = prices[token.id]?.usd || 0;
      let totalQuantity = 0;

      token.positions?.forEach(position => {
        if (position.type === 'buy') {
          totalQuantity += position.quantity;
        } else if (position.type === 'sell') {
          totalQuantity -= position.quantity;
        }
      });

      const currentValue = currentPrice * totalQuantity;
      const currentPercent = totalValue > 0 ? (currentValue / totalValue) * 100 : 0;
      const targetPercent = targetAllocations[token.id] || 0;
      const difference = targetPercent - currentPercent;
      const differencePercent = currentPercent > 0 ? (difference / currentPercent) * 100 : 0;

      let action: 'buy' | 'sell' | 'hold' = 'hold';
      if (Math.abs(difference) > rebalanceThreshold) {
        action = difference > 0 ? 'buy' : 'sell';
      }

      const targetValue = (targetPercent / 100) * totalValue;
      const amountUSD = Math.abs(targetValue - currentValue);
      const amount = currentPrice > 0 ? amountUSD / currentPrice : 0;

      allocations.push({
        tokenId: token.id,
        tokenName: token.displayName,
        symbol: token.displayName.toUpperCase(),
        currentValue,
        currentPercent,
        targetPercent,
        difference,
        differencePercent,
        action,
        amount,
        amountUSD
      });
    });

    // Calculate trades needed
    const trades: RebalanceTrade[] = allocations
      .filter(alloc => alloc.action !== 'hold')
      .map(alloc => {
        const currentPrice = prices[alloc.tokenId]?.usd || 0;
        return {
          tokenId: alloc.tokenId,
          tokenName: alloc.tokenName,
          symbol: alloc.symbol,
          action: alloc.action,
          quantity: alloc.amount,
          valueUSD: alloc.amountUSD,
          currentPrice
        };
      })
      .sort((a, b) => b.valueUSD - a.valueUSD);

    return {
      totalValue,
      allocations: allocations.sort((a, b) => b.currentValue - a.currentValue),
      trades,
      needsRebalancing: trades.length > 0
    };
  }, [tokens, prices, targetAllocations, rebalanceThreshold]);

  const updateTargetAllocation = (tokenId: string, value: number) => {
    setTargetAllocations(prev => ({
      ...prev,
      [tokenId]: Math.max(0, Math.min(100, value))
    }));
  };

  const distributeEqually = () => {
    const equalAllocation = 100 / tokens.length;
    const newTargets: Record<string, number> = {};
    tokens.forEach(token => {
      newTargets[token.id] = equalAllocation;
    });
    setTargetAllocations(newTargets);
  };

  const fmt = (n: number, decimals = 2) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const totalTargetAllocation = Object.values(targetAllocations).reduce((sum, val) => sum + val, 0);
  const isValid = Math.abs(totalTargetAllocation - 100) < 0.01;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Icons.Target size={32} color="var(--accent)" />
          Portfolio Rebalancing
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
          Maintain your target asset allocation effortlessly. Set targets and get trade suggestions.
        </p>
      </div>

      {/* Settings */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600 }}>Rebalancing Settings</h2>
          <button
            onClick={distributeEqually}
            style={{
              padding: '8px 16px',
              background: 'var(--accent-light)',
              color: 'var(--accent)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            Distribute Equally
          </button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-secondary)' }}>
            Rebalance Threshold (%)
          </label>
          <input
            type="number"
            min="1"
            max="50"
            value={rebalanceThreshold}
            onChange={(e) => setRebalanceThreshold(Number(e.target.value))}
            style={{
              width: '200px',
              padding: '10px 12px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '14px'
            }}
          />
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
            Rebalance only when allocation differs by more than this threshold
          </p>
        </div>

        {/* Allocation Warning */}
        {!isValid && (
          <div style={{
            padding: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icons.AlertTriangle size={16} color="#ef4444" />
            <span style={{ fontSize: '13px', color: '#ef4444' }}>
              Total allocation must equal 100% (currently {fmt(totalTargetAllocation)}%)
            </span>
          </div>
        )}
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Portfolio Value</div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>${fmt(metrics.totalValue, 0)}</div>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Assets to Rebalance</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: metrics.needsRebalancing ? '#fbbf24' : '#10b981' }}>
            {metrics.trades.length}
          </div>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Status</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: metrics.needsRebalancing ? '#fbbf24' : '#10b981' }}>
            {metrics.needsRebalancing ? 'Needs Rebalancing' : 'Balanced'}
          </div>
        </div>
      </div>

      {/* Allocations Table — V2.I.7.e migrated to ResponsiveTable */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Asset Allocations</h3>
        <ResponsiveTable<AssetAllocation>
          data={metrics.allocations}
          keyExtractor={(a) => a.tokenId}
          columns={[
            {
              key: 'asset',
              header: 'Asset',
              isTitle: true,
              render: (a) => <div style={{ fontWeight: 600 }}>{a.tokenName}</div>,
            },
            {
              key: 'currentValue',
              header: 'Current Value',
              align: 'right',
              render: (a) => `$${fmt(a.currentValue, 0)}`,
            },
            {
              key: 'currentPercent',
              header: 'Current %',
              align: 'right',
              render: (a) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                  <div style={{
                    width: '80px',
                    height: '6px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(100, a.currentPercent)}%`,
                      height: '100%',
                      background: 'var(--accent)',
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <span>{fmt(a.currentPercent)}%</span>
                </div>
              ),
            },
            {
              key: 'targetPercent',
              header: 'Target %',
              align: 'right',
              render: (a) => (
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={fmt(a.targetPercent, 1)}
                  onChange={(e) => updateTargetAllocation(a.tokenId, Number(e.target.value))}
                  aria-label={`Target percent for ${a.tokenName}`}
                  style={{
                    width: '70px',
                    padding: '6px 8px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    textAlign: 'right',
                  }}
                />
              ),
            },
            {
              key: 'difference',
              header: 'Difference',
              align: 'right',
              render: (a) => (
                <span style={{
                  color: a.difference > 0 ? '#10b981' : a.difference < 0 ? '#ef4444' : 'var(--text-secondary)',
                }}>
                  {a.difference > 0 ? '+' : ''}{fmt(a.difference, 1)}%
                </span>
              ),
            },
            {
              key: 'action',
              header: 'Action',
              align: 'center',
              render: (a) => a.action === 'hold' ? (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: 'var(--accent)',
                }}>
                  HOLD
                </span>
              ) : (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: a.action === 'buy' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  color: a.action === 'buy' ? '#10b981' : '#ef4444',
                }}>
                  {a.action === 'buy' ? '↗ BUY' : '↙ SELL'}
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* Trade Suggestions */}
      {metrics.needsRebalancing && isValid && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600 }}>Required Trades</h3>
            <button
              onClick={() => setShowTrades(!showTrades)}
              style={{
                padding: '8px 16px',
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {showTrades ? 'Hide Trades' : 'Show Trades'}
            </button>
          </div>

          {showTrades && (
            <>
              <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icons.AlertTriangle size={16} color="#fbbf24" />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Review all trades carefully. Execute them manually in your exchange.
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {metrics.trades.map((trade, index) => (
                  <div
                    key={trade.tokenId}
                    style={{
                      padding: '16px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                        {index + 1}. {trade.action === 'buy' ? '🟢 Buy' : '🔴 Sell'} {trade.tokenName}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        Quantity: {fmt(trade.quantity, 4)} {trade.symbol} @ ${fmt(trade.currentPrice)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>${fmt(trade.valueUSD, 0)}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Total Value</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {!metrics.needsRebalancing && (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Icons.Check size={48} style={{ margin: '0 auto 16px', color: '#10b981' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#10b981' }}>Portfolio is Balanced</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            All assets are within {rebalanceThreshold}% of their target allocations.
          </p>
        </div>
      )}
    </div>
  );
}
